import { TradeBook } from '@app/modules/trading/modules/books/books.models';
import { OrderMode, ORDER_CONDITION } from '@app/_shared/shared.enums';
import { Quote } from '@shared/providers';
import { AlgoStrategy } from '../strategies';
import { DEFAULT_PRECISION, Utils } from '@app/_helpers';
import { WebAlertCondition } from '../../../../utProto/webAlertStructureEnums_pb';
import { floor } from 'lodash-es';
import { EventEmitter } from '@angular/core';

export class AlgoSymbolPosition {
    totalExecutedLots = 0;
    totalBuyValue = 0;
    totalSellValue = 0;
    totalBuyQuantity = 0;
    totalSellQuantity = 0;
    averageSellPrice = 0;
    averageBuyPrice = 0;
    openQuantity = 0;
    mtm = 0;
    closeQty = 0;
    bookedPL = 0;
    net_value = 0;
    totalPL = 0;
    GLP = 0;
    totalLots = 0;
    initialAmount = 0;
    market_lot = 1;
    itr = 1;
    avgEntryPrice = 0;
    scrip_name = '';
    product = '';
    market_id = '';
    broker = '';
    net_pnl = 0;
    target_profit = null;
    target_profit_type = 0;
    stop_loss = null;
    stop_loss_type = 0;
    net_lots = 0;
    position_type = '';
    sell_lots = 0;
    buy_lots = 0;
    ltp = 0;
    // Quote Object
    quoteObj: Quote;
    security_id = 0;
    leg_id = '';
    tradeIdList = new Map<string, string>();
    ucc?: string;
    client_name?: string;
    closePrice = 0;

    mtmUpdated = new EventEmitter()

    constructor(quoteObj: Quote, algo: AlgoStrategy, symbolInfo: number[]) {
        if (symbolInfo) {
            this.market_lot = symbolInfo[0];
        }
        this.quoteObj = quoteObj;
        this.updateClosePrice(quoteObj, symbolInfo);
        this.quoteObj.ltpUpdate.subscribe(() => {
            this.ltp = quoteObj.LTP;
            this.updateClosePrice(quoteObj, symbolInfo);
            this.updateMTM();
            this.updateStrategyDetails(algo);
        });
    }

    updateClosePrice(quoteObj: Quote, symbolInfo: number[]) {
        // update close price if ltp is not coming
        if (!quoteObj.LTP) {
            // In case close price is not available in quote object, use close price from symbol info (scrip.js)
            if (quoteObj.closePrice) {
                this.closePrice = quoteObj.closePrice;
            } else {
                this.closePrice = symbolInfo[3];
            }
        }
    }

    updateStrategyDetails(algo: AlgoStrategy) {
        if (algo) {
            this.calcLots(algo);
        }
    }

    calcLots(algo: any) {
        if (!algo.isLot) {
            const divisor = (Utils.applyPrecision(this.quoteObj.LTP || this.quoteObj.closePrice, DEFAULT_PRECISION)) || 1;
            this.totalLots = floor(algo.orderQty / (divisor * this.market_lot)) * algo.algoCycles;
        } else {
            this.totalLots = algo.valueConvertedLot[0] * algo.algoCycles;
        }
    }
    /**
     * Total Executed Lots = Total Filled Quantity / Lot Size
     * Open Quantity = Buy Quantity - Sell Quantity
     * Booked Quantity/Close Quantity = min(Buy Traded Quantity, Sell Traded Quantity )
     * Booked Profit Loss = (Avg Sell Price - Avg Buy Price) * Booked Quantity
     * MTM = Open Quantity * Profit (Profit = Current Price - Buy/Sell Price)
     * Gain Loss Percent = (Booked Profit Loss / Initial Total Buy/Sell Price) * 100
    */

    updateTrade(trade: TradeBook) {

        // checking for unique trade id in tradeidList map which ignores the repeatation of duplicate trades. 
        if (this.tradeIdList.has(trade.trade_id)) {
            return;
        }

        this.tradeIdList.set(trade.trade_id, trade.trade_id);

        this.market_id = trade.market_id;
        this.scrip_name = trade.scrip_name;
        this.broker = trade.broker;
        this.product = trade.product_type;
        this.position_type = trade.strategy_type;
        this.security_id = trade.security_id;
        this.leg_id = trade.leg_id;
        if (trade?.lot_size) {
            this.market_lot = trade.lot_size;
        }


        if (trade.order_condition == ORDER_CONDITION[WebAlertCondition.ALERTCONDITION_ENTRY]) {
            this.totalExecutedLots += trade.filled_lots;
            this.totalExecutedLots = Math.round(this.totalExecutedLots);
            this.initialAmount += trade.filled_price * trade.filled_quantity;
            this.avgEntryPrice = parseFloat((this.initialAmount / (this.totalExecutedLots * this.market_lot)).toFixed(DEFAULT_PRECISION));
            this.avgEntryPrice = this.avgEntryPrice === Infinity ? 0 : this.avgEntryPrice;
        }
        if (trade.order_mode == OrderMode.BUY.toUpperCase()) {
            this.totalBuyValue += (trade.filled_quantity * trade.filled_price);
            this.totalBuyQuantity += trade.filled_quantity;
        } else {
            this.totalSellValue += (trade.filled_quantity * trade.filled_price);
            this.totalSellQuantity += trade.filled_quantity;
        }

        this.averageSellPrice = this.totalSellValue / this.totalSellQuantity || 0;
        this.averageBuyPrice = this.totalBuyValue / this.totalBuyQuantity || 0;
        this.net_value = this.totalSellValue - this.totalBuyValue
        this.openQuantity = Math.round((this.totalBuyQuantity - this.totalSellQuantity) / this.market_lot);
        this.closeQty = Math.min(this.totalBuyQuantity, this.totalSellQuantity);
        this.bookedPL = parseFloat(((this.averageSellPrice - this.averageBuyPrice) * this.closeQty).toString());
        this.sell_lots = this.totalSellQuantity / this.market_lot || 0;
        this.buy_lots = this.totalBuyQuantity / this.market_lot || 0;
        if (trade.ucc && trade.client_name) {
            this.ucc = trade.ucc;
            this.client_name = trade.client_name;
        }
        this.updateMTM();
    }

    updateMTM() {
        if (this.quoteObj.LTP) {
            if (this.openQuantity > 0) {
                this.mtm = parseFloat((Math.abs(this.openQuantity) * this.market_lot * ((this.quoteObj.LTP) - this.averageBuyPrice)).toString());
            } else {
                this.mtm = parseFloat((Math.abs(this.openQuantity) * this.market_lot * (this.averageSellPrice - (this.quoteObj.LTP))).toString());
            }

            this.totalPL = parseFloat((this.mtm + this.bookedPL).toString());

            this.GLP = (this.closeQty * this.avgEntryPrice) ? parseFloat(((this.bookedPL / (this.closeQty * this.avgEntryPrice))
                * 100).toFixed(DEFAULT_PRECISION)) : 0;
            this.net_pnl = (this.bookedPL + this.mtm || 0);
            this.mtmUpdated.emit();
        }
    }
}
